import React, { useEffect } from 'react';
import { ENTITIES, definitionAsRow } from '../lib/row-definition-ml';
import { POOL_REFINEMENT } from '../lib/row-definition-ml';
import { FILTER_ENTITY } from '../lib/row-definition-ml';
import { SELECTION } from '../lib/row-definition-ml';
import { SORT_RESULTS } from '../lib/row-definition-ml';
import { TIMEFRAME } from '../lib/row-definition-ml';
import { allowedOptions } from '../lib/row-definition-helpers';
import Select from 'react-select';
import { useRowForm } from '../RowFormContext';
import './RowDefinitionBuilder.scss';

const allowedOptionsAsSelectOptions = (definition, options) => allowedOptions(definition, options).map(opt => ({ label: opt.label, value: opt.key }));

export const RowDefinitionBuilderML = () => {
  const { setFilter, setCardSort, setEntity, definition, setDefinition } = useRowForm();

  const allowedPoolRefinementOptions = allowedOptionsAsSelectOptions(definition, POOL_REFINEMENT);
  const allowedFilterEntityOptions = allowedOptionsAsSelectOptions(definition, FILTER_ENTITY);
  const allowedSelectionOptions = allowedOptionsAsSelectOptions(definition, SELECTION);
  const allowedSortResultsOptions = allowedOptionsAsSelectOptions(definition, SORT_RESULTS);
  const allowedTimeframeOptions = allowedOptionsAsSelectOptions(definition, TIMEFRAME);

  /*
   * Auto-select fields when only one valid option exists
   */
  useEffect(() => {
    const autoSelects = {
      poolRefinement: allowedPoolRefinementOptions,
      filterEntity: allowedFilterEntityOptions,
      selection: allowedSelectionOptions,
      sortResults: allowedSortResultsOptions,
      timeframe: allowedTimeframeOptions
    };

    const updates = {};

    Object.entries(autoSelects).forEach(([field, options]) => {
      if (options.length === 1 && definition[field] !== options[0].value) {
        updates[field] = options[0].value;
      }
    });

    if (Object.keys(updates).length > 0) {
      setDefinition(prev => ({ ...prev, ...updates }));
    }
  }, [
    allowedPoolRefinementOptions,
    allowedFilterEntityOptions,
    allowedSelectionOptions,
    allowedSortResultsOptions,
    allowedTimeframeOptions,
    definition
  ]);

  useEffect(() => {
    const asRow = definitionAsRow(definition);

    setEntity(asRow.entity);
    setFilter(asRow.filter);
    setCardSort(asRow.cardSort);
  }, [definition, setFilter, setCardSort, setEntity]);

  return (
    <div className='row-definition-builder'>
      <label>
        <span className='label-text'>Entity</span>
        <Select
          className='select'
          options={Object.values(ENTITIES).map(entity => ({ label: entity, value: entity }))}
          placeholder='Select entity'
          value={definition.entity ? { label: definition.entity, value: definition.entity } : undefined}
          onChange={option => setDefinition(prev => ({ ...prev, entity: option?.value || null }))}
        />
      </label>

      {allowedPoolRefinementOptions.length > 1 && (
        <label>
          <span className='label-text'>within</span>
          <Select
            className='select'
            options={allowedPoolRefinementOptions}
            placeholder='Select pool refinement'
            value={allowedPoolRefinementOptions.find(opt => opt.value === definition.poolRefinement) || undefined}
            onChange={option => setDefinition(prev => ({ ...prev, poolRefinement: option?.value || null }))}
          />
        </label>
      )}

      {allowedFilterEntityOptions.length > 1 && (
        <label>
          <span className='label-text'>filtered by</span>
          <Select
            className='select'
            options={allowedFilterEntityOptions}
            placeholder='Select entity filter'
            value={allowedFilterEntityOptions.find(opt => opt.value === definition.filterEntity) || undefined}
            onChange={option => setDefinition(prev => ({ ...prev, filterEntity: option?.value || null }))}
          />
        </label>
      )}

      {allowedSelectionOptions.length > 1 && (
        <label>
          <span className='label-text'>using</span>
          <Select
            className='select'
            options={allowedSelectionOptions}
            placeholder='Select selection'
            value={allowedSelectionOptions.find(opt => opt.value === definition.selection) || undefined}
            onChange={option => setDefinition(prev => ({ ...prev, selection: option?.value || null }))}
          />
        </label>
      )}

      {allowedSortResultsOptions.length > 1 && (
        <label>
          <span className='label-text'>sorted by</span>
          <Select
            className='select'
            options={allowedSortResultsOptions}
            placeholder='Select sort results'
            value={allowedSortResultsOptions.find(opt => opt.value === definition.sortResults) || undefined}
            onChange={option => setDefinition(prev => ({ ...prev, sortResults: option?.value || null }))}
          />
        </label>
      )}

      {allowedTimeframeOptions.length > 1 && (
        <label>
          <span className='label-text'>timeframe</span>
          <Select
            className='select'
            options={allowedTimeframeOptions}
            placeholder='Select timeframe'
            value={allowedTimeframeOptions.find(opt => opt.value === definition.timeframe) || undefined}
            onChange={option => setDefinition(prev => ({ ...prev, timeframe: option?.value || null }))}
          />
        </label>
      )}
    </div>
  );
};
